import { Link } from "react-router-dom";
import {
  FileText,
  Image,
  LayoutDashboard,
  LogOut,
  Mail,
  Package,
  ReceiptText,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Tags,
  TicketPercent,
  Users,
  X,
} from "lucide-react";

const navItems = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { path: "/admin/products", label: "Products", icon: Package },
  { path: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { path: "/admin/payment-notices", label: "Payment Notices", icon: ReceiptText },
  { path: "/admin/categories", label: "Categories", icon: Tags },
  { path: "/admin/coupons", label: "Coupons", icon: TicketPercent },
  { path: "/admin/banners", label: "Banners", icon: Image },
  { path: "/admin/about", label: "About Us", icon: FileText },
  { path: "/admin/rkicha-home", label: "RKICHA Home", icon: ShoppingBag },
  { path: "/admin/messages", label: "Messages", icon: Mail },
  { path: "/admin/settings", label: "Settings", icon: Settings },
  { path: "/admin/admins", label: "Admins", icon: ShieldCheck },
  { path: "/admin/users", label: "Users", icon: Users },
];

const isItemActive = (item, currentPath) => {
  if (item.exact) return currentPath === item.path;
  return currentPath.startsWith(item.path);
};

const SidebarContent = ({ currentPath, onNavigate, onLogout }) => (
  <>
    <div className="flex h-20 items-center gap-3 border-b border-white/10 px-5">
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#8B5E46] text-white shadow-lg shadow-black/20">
        <ShoppingBag className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <Link
          to="/admin"
          onClick={onNavigate}
          className="block text-lg font-black lowercase tracking-wide text-white"
        >
          rkicha admin
        </Link>
        <p className="text-xs font-semibold text-[#D6C2B8]">Ecommerce control</p>
      </div>
    </div>

    <nav className="custom-scrollbar flex-1 space-y-1 overflow-y-auto px-3 py-5" aria-label="Admin navigation">
      {navItems.map(item => {
        const Icon = item.icon;
        const active = isItemActive(item, currentPath);

        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={`group flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all focus-visible:ring-2 focus-visible:ring-[#D9B29F] ${
              active
                ? "bg-gradient-to-r from-[#8B5E46] to-[#6F4635] text-white shadow-lg shadow-black/20"
                : "text-[#BCA89D] hover:bg-white/10 hover:text-white"
            }`}
            aria-current={active ? "page" : undefined}
          >
            <Icon
              className={`h-5 w-5 flex-shrink-0 ${active ? "text-[#FCE8DE]" : "text-[#CBB6AA] group-hover:text-white"}`}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">{item.label}</span>
            {item.badge ? (
              <span className="rounded-full bg-[#EADFD7] px-2 py-0.5 text-[11px] font-black text-[#3A2922]">
                {item.badge}
              </span>
            ) : null}
          </Link>
        );
      })}
    </nav>

    <div className="border-t border-white/10 p-3">
      <button
        type="button"
        onClick={onLogout}
        className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold text-[#CBB6AA] transition-all hover:bg-red-500/10 hover:text-red-200 focus-visible:ring-2 focus-visible:ring-red-200"
      >
        <LogOut className="h-5 w-5" aria-hidden="true" />
        <span>Logout</span>
      </button>
    </div>
  </>
);

const AdminSidebar = ({ currentPath, mobileOpen, onCloseMobile, onLogout }) => (
  <>
    <aside className="fixed left-0 top-0 z-40 hidden h-full w-[260px] flex-col overflow-hidden bg-gradient-to-b from-[#1F1714] via-[#2B1E1A] to-[#3A2922] text-white shadow-2xl lg:flex">
      <SidebarContent currentPath={currentPath} onLogout={onLogout} />
    </aside>

    <div
      className={`fixed inset-0 z-[70] bg-stone-950/50 backdrop-blur-sm transition-opacity lg:hidden ${
        mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
      onClick={onCloseMobile}
      aria-hidden="true"
    />

    <aside
      className={`fixed left-0 top-0 z-[80] flex h-full w-[288px] flex-col overflow-hidden bg-gradient-to-b from-[#1F1714] via-[#2B1E1A] to-[#3A2922] text-white shadow-2xl transition-transform duration-300 lg:hidden ${
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      }`}
      aria-label="Mobile admin navigation"
    >
      <button
        type="button"
        onClick={onCloseMobile}
        className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/15 focus-visible:ring-2 focus-visible:ring-white"
        aria-label="Close navigation"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>
      <SidebarContent currentPath={currentPath} onNavigate={onCloseMobile} onLogout={onLogout} />
    </aside>
  </>
);

export default AdminSidebar;
